import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGlobalContext } from 'GlobalContext';
import { useModal } from 'components/modals/ModalContext';
import { saveData } from 'services/api/saveData';
import TextInput from 'components/forms/basefields/TextInput';
import PrimaryButton from 'components/buttons/PrimaryButton';
import SecondaryButton from 'components/buttons/SecondaryButton';
import { Ticket } from './TicketTypes';

interface TicketReporterEmailModalProps {
    ticket: Ticket;
    onSaved: () => void;
}

// The modal to enter the email address of a requester without one
const TicketReporterEmailModal: React.FC<TicketReporterEmailModalProps> = ({ ticket, onSaved }) => {
    const { t } = useTranslation();
    const { setFloatingAlert } = useGlobalContext();
    const { revealModal, closeModal } = useModal();
    const [email, setEmail] = useState('');
    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
    const [loading, setLoading] = useState(false);

    // Reveal the modal on the first render
    useEffect(() => { revealModal(); }, []);

    // Save the email address on the requester and the ticket
    const handleSave = async () => {
        setLoading(true);
        setErrorMessage(undefined);
        try {
            const response = await saveData({ apiUrl: `post_ticket_reporter_email/${ticket.id}`, method: 'post', data: { email } });
            if (response?.status === 200) {
                setFloatingAlert({ type: 'success' });
                onSaved();
                closeModal();
                return;
            }
            setErrorMessage(t('validation.email.valid_email_message'));
        } catch (error: any) {
            setErrorMessage(t(error?.response?.data?.email || 'validation.email.valid_email_message'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className='formset add-edit-form'>
            <div className='formfield'>
                <label>{t('ticket.general.requester_email_label')}</label>
                <TextInput
                    name='email'
                    placeholder='ticket.general.reporter_email_placeholder'
                    value={email}
                    shouldAutoFocus={true}
                    errorMessage={errorMessage}
                    onChange={(value) => setEmail(value)}
                />
                {errorMessage && <p className='error-message'>{errorMessage}</p>}
            </div>
            <div className='buttons-right'>
                <SecondaryButton onClick={closeModal} label='general.cancel' size='small' />
                <PrimaryButton onClick={handleSave} label='general.save' size='small' loading={loading} onlyViewRestriction={true} />
            </div>
        </div>
    );
};

export default TicketReporterEmailModal;
